// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。

// I15/I16：学习空间 API client（C1 提交/投影 + C2 目标/计划 typed 面）。

import { ApiBase } from './apiBase';

export interface SubmissionReceipt {
  submission_id: string;
  event_ref: string;
  activity_revision: number;
  persisted: boolean;
  replayed: boolean;
  projection_status: 'projected' | 'pending_projection';
}

export interface ActivityProjectionState {
  last_seq: number;
  prediction: { seq: number; student_input: Record<string, unknown> } | null;
  observation: { seq: number; student_input: Record<string, unknown> } | null;
  explanation: { seq: number; student_input: Record<string, unknown> } | null;
  missing: string[];
  activity_complete: boolean;
  feedback_gate: string;
  mastery_updated: boolean;
}

export interface ActivityProjection {
  thread_id: string;
  activity_id: string;
  activity_revision: number;
  projection_status: 'projected' | 'pending_projection';
  state: ActivityProjectionState;
}

export interface LearningGoal {
  goal_id: string;
  title: string;
  status: 'draft' | 'accepted' | 'retired';
  revision: number;
  objective_ids: string[];
  prereq_edges: [string, string][];
  updated_at: string | null;
}

export interface PlanTask {
  task_id: string;
  objective_id: string;
  title: string;
  status: 'pending' | 'active' | 'completed' | 'skipped';
  evidence_refs: string[];
  prereq_task_ids: string[];
  due_at?: string | null;
}

export interface StudyPlan {
  plan_id: string;
  goal_id: string;
  status: 'accepted' | 'active' | 'completed' | 'paused';
  revision: number;
  tasks: PlanTask[];
  next_action?: {
    decision_id: string;
    task_id: string | null;
    action_kind: string;
    reason: string;
    evidence_refs: string[];
    expected_next_evidence: string;
    policy_revision: string;
  };
}

export class ApiLearning extends ApiBase {
  async getActivityProjection(threadId: string, activityId: string) {
    const response = await this.client.get(`/v1/learning/activities/${threadId}/${activityId}`);
    return response.data as ActivityProjection;
  }

  async listGoals(threadId: string, status?: 'draft' | 'accepted' | 'retired') {
    const response = await this.client.get('/v1/learning/goals', {
      params: { thread_id: threadId, ...(status ? { status } : {}) },
    });
    return response.data as { goals: LearningGoal[] };
  }

  async getGoal(goalId: string, threadId: string) {
    const response = await this.client.get(`/v1/learning/goals/${goalId}`, {
      params: { thread_id: threadId },
    });
    return response.data as LearningGoal;
  }

  async acceptGoal(goalId: string, threadId: string, expectedRevision?: number) {
    const response = await this.client.patch(`/v1/learning/goals/${goalId}/accept`, {
      thread_id: threadId,
      expected_revision: expectedRevision,
    });
    return response.data as LearningGoal;
  }

  async getPlan(planId: string, threadId: string) {
    const response = await this.client.get(`/v1/learning/plans/${planId}`, {
      params: { thread_id: threadId },
    });
    return response.data as StudyPlan;
  }

  async listPlans(goalId: string, threadId: string) {
    const response = await this.client.get('/v1/learning/plans', {
      params: { goal_id: goalId, thread_id: threadId },
    });
    return response.data as { plans: StudyPlan[] };
  }

  async getClaimAudit(auditId: string, threadId: string) {
    const response = await this.client.get(`/v1/paper/claim-audits/${auditId}`, {
      params: { thread_id: threadId },
    });
    return response.data as ClaimAuditView;
  }
}

export interface ClaimAuditView {
  audit_id: string;
  draft_id: string;
  draft_hash: string;
  source_manifest_digest: string;
  policy_revision: string;
  coverage: string;
  results: {
    claim_id: string;
    support_status: string;
    reason: string;
    source_ref?: Record<string, unknown>;
  }[];
}

// I17：主张审计只读取数（agent 登记表进程内；404=无登记审计→空态）
export async function getClaimAudit(
  baseUrl: string,
  auditId: string,
  threadId: string,
): Promise<ClaimAuditView | null> {
  const response = await fetch(
    `${baseUrl}/api/v1/paper/claim-audits/${auditId}?thread_id=${encodeURIComponent(threadId)}`,
    {
    headers: { Authorization: `Bearer ${localStorage.getItem('auth-token') ?? ''}` },
    },
  );
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`claim audit fetch failed: ${response.status}`);
  return (await response.json()) as ClaimAuditView;
}
